import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import Topbar from "./Topbar";
import ReportsFunnel from "./ReportsFunnel";
import ReportsScope from "./ReportsScope";
import ReportsFilters from "./ReportsFilters";
import ReportsTiles from "./ReportsTiles";
import ReportsDrill from "./ReportsDrill";
import ReportsBreakdowns from "./ReportsBreakdowns";
import ReportsMembers from "./ReportsMembers";

/*
 * REPORTS v2 — three bands at full content width.
 *
 *   1  funnel        fixed, not reorderable, not removable
 *   2  metric tiles  the user's config: which tiles, in what order
 *   3  breakdowns    by source, then why deals closed out
 *
 * with a drill-down chart that opens between bands 2 and 3 when a tile is asked
 * to expand.
 *
 * Everything rendered comes from ONE call to /reports/metrics, which is one call
 * to src/reports/metrics.js. That is deliberate and is the whole architecture of
 * the rebuild: this file positions things, holds the filters, and persists two
 * preferences. It does not decide whether a percentage may be printed, whether a
 * period is complete, whether a base is large enough or whether an alert is
 * warranted — v1 decided all four here, in the view, which is why the page was
 * wrong in four separate ways at once.
 *
 * FILTERS COMPOSE, AND THEY LIVE HERE. Stage and source are two independent
 * narrowings, held as two pieces of state and sent as two query parameters, and
 * the metric layer applies them — nothing here re-derives a filtered number.
 *
 * They do not reach equally far, and the page says so rather than pretending
 * otherwise. Source narrows everything: the funnel, the breakdown tables, the
 * tiles and the drill-down chart. Stage narrows the cohort objects only, because
 * a stage is a predicate about a company's progress within the SCOPE window
 * while the tiles are event counts over twelve months — "leads in October, among
 * companies that got priced within 60 days of a call within 30 days of entering,
 * in the last 90 days" is not a number anyone could check. `tiles_filtered_by`
 * on the payload says which filters got through, and the tiles band prints a
 * note when one did not.
 *
 * WHAT v1 LEFT BEHIND. The report_metrics setting still exists and still drives
 * /reports/trends and the configure_reports MCP tool; this page no longer renders
 * it. See docs/OWED.md.
 */

// Scope resolves server-side (query → saved preference → 90 days), so the first
// fetch deliberately sends nothing and reads the answer back off the payload.
const SCOPE_FALLBACK = 90;

export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // null until the first payload comes back and tells us the saved scope.
  const [scopeDays, setScopeDays] = useState(null);
  // A custom {from, to} range, or null when a preset is in force. The two are
  // mutually exclusive: picking a preset clears the range, and vice versa.
  const [customRange, setCustomRangeState] = useState(null);
  const [stage, setStage] = useState(null);
  const [source, setSource] = useState(null);
  // Which tile is expanded into the drill-down chart, or null.
  const [drillId, setDrillId] = useState(null);
  // Which stage's company list is open over the page, or null.
  const [membersFor, setMembersFor] = useState(null);
  // Set when the list was opened from ONE BAR of the drill-down chart, so it
  // shows that month or week rather than the whole scope.
  const [membersPeriod, setMembersPeriod] = useState(null);
  // The metric grid: cards and charts, in order, with widths. See ReportsTiles.
  const [layout, setLayoutState] = useState(null);

  const load = useCallback(async (days, range, stageFilter, sourceFilter) => {
    const query = new URLSearchParams();
    // A range and a preset must never both be sent: the server would have to
    // guess which the reader meant, and it would guess in one place while the
    // button showed the other.
    if (range) {
      query.set("from", range.from);
      query.set("to", range.to);
    } else if (days) {
      query.set("scope_days", String(days));
    }
    if (stageFilter) query.set("stage", stageFilter);
    if (sourceFilter) query.set("source", sourceFilter);
    const suffix = query.toString();
    return api(`/reports/metrics${suffix ? `?${suffix}` : ""}`);
  }, []);

  useEffect(() => {
    let live = true;
    setBusy(true);
    load(scopeDays, customRange, stage, source)
      .then((next) => {
        if (!live) return;
        setReport(next);
        setError("");
        setScopeDays((current) => current ?? next.scope.days ?? SCOPE_FALLBACK);
        setLayoutState((current) => current ?? next.layout);
        // A saved range comes back on the first load; adopt it so the button
        // shows the dates the numbers actually came from.
        // from_date/to_date, never from/to: `to` is the exclusive midnight after
        // the chosen end, so re-sending it pushed the window one day further out
        // on every reload.
        if (next.scope.custom) {
          setCustomRangeState((current) => current ?? {
            from: next.scope.from_date,
            to: next.scope.to_date,
          });
        }
      })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [load, scopeDays, customRange, stage, source]);

  /*
   * Preferences are written optimistically and rolled back on failure. The tile
   * row is the reason: a drag that waits for a round trip before moving the tile
   * feels broken, and a drag that moves and then silently reverts is worse.
   */
  const persist = useCallback(async (body, rollback) => {
    try {
      await api("/settings", { method: "PUT", body: JSON.stringify(body) });
    } catch (e) {
      setError(e.message);
      rollback();
    }
  }, []);

  const setScope = (days) => {
    const previousDays = scopeDays;
    const previousRange = customRange;
    setScopeDays(days);
    setCustomRangeState(null);
    persist({ reports_scope_days: days }, () => {
      setScopeDays(previousDays);
      setCustomRangeState(previousRange);
    });
  };

  const setCustomRange = (range) => {
    const previous = customRange;
    setCustomRangeState(range);
    persist({ reports_scope_custom: range }, () => setCustomRangeState(previous));
  };

  const setLayout = (next) => {
    const previous = layout;
    setLayoutState(next);
    persist({ reports_layout: next }, () => setLayoutState(previous));
  };

  // Any stat opens in the chart; the same stat again closes it.
  const expand = (id) => setDrillId((current) => (current === id ? null : id));

  const header = (
    <Topbar title="Reports">
      <ReportsFilters
        stage={stage}
        source={source}
        onClearStage={() => setStage(null)}
        onClearSource={() => setSource(null)}
      />
      <ReportsScope
        choices={report?.scope_choices || []}
        value={scopeDays ?? SCOPE_FALLBACK}
        custom={customRange}
        onChange={setScope}
        onCustom={setCustomRange}
        busy={busy && !report}
      />
    </Topbar>
  );

  if (!report || !layout) {
    return (
      <>
        {header}
        <div className="crm-reports">{error ? <p className="crm-login__err">{error}</p> : "Loading…"}</div>
      </>
    );
  }

  // Every tile carries a series now (src/reports/metrics.js scopeTiles), so any
  // of them can open in the chart.
  const drillTile = drillId ? report.tiles[drillId] : null;

  return (
    <>
      {header}
      <div className={`crm-reports${busy ? " is-busy" : ""}`}>
        {error && <p className="crm-login__err" style={{ marginTop: 0 }}>{error}</p>}

        <ReportsFunnel
          funnel={report.funnel}
          /* Attributed sources only — "across 4 sources" counting the
             unattributed bucket claims an attribution that does not exist. */
          sourceCount={
            (report.by_source?.rows || []).filter((r) => r.leads > 0 && r.source !== "unattributed").length
          }
          filters={report.filters}
          selected={drillId}
          /*
           * A stage or a rate click opens it in the chart (Gabriel, 2026-10-07:
           * "change the chart by clicking on ANY stat"). The company list it
           * used to open is the chart's "Companies" button, one click further,
           * and a bar opens the companies of that period.
           */
          onSelectStage={expand}
        />

        <ReportsTiles
          tiles={report.tiles}
          library={report.tile_library}
          layout={layout}
          expandedId={drillId}
          filters={report.filters}
          filteredBy={report.tiles_filtered_by}
          onLayout={setLayout}
          onExpand={expand}
          onSelectPeriod={(tile, p) => { setMembersPeriod(p); setMembersFor(tile.id); }}
        />

        {/* Scrolls itself into view when a stat elsewhere on the page opens it. */}
        {drillTile && drillTile.series && (
          <ReportsDrill
            tile={drillTile}
            onSelectPeriod={(p) => { setMembersPeriod(p); setMembersFor(drillTile.id); }}
            onShowMembers={(id) => { setMembersPeriod(null); setMembersFor(id); }}
            onClose={() => setDrillId(null)}
          />
        )}

        <ReportsBreakdowns
          bySource={report.by_source}
          lost={report.lost_reasons}
          selectedSource={source}
          onSelectSource={(next) => setSource((current) => (current === next ? null : next))}
        />
      </div>

      {membersFor && (
        <ReportsMembers
          stage={membersFor}
          scope={report.scope}
          period={membersPeriod}
          source={source}
          filterActive={stage === membersFor}
          onFilter={() => {
            setStage((current) => (current === membersFor ? null : membersFor));
            setMembersFor(null);
            setMembersPeriod(null);
          }}
          onClose={() => { setMembersFor(null); setMembersPeriod(null); }}
        />
      )}
    </>
  );
}
